import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import apiClient, { jobsAPI, plansAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft, Save, Send, Star, CheckCircle2, Wallet, AlertTriangle,
  Lightbulb, ShoppingBag, Sparkles, Zap
} from 'lucide-react';

import { JOB_CATEGORIES as CATEGORIES, JOB_CATEGORY_TO_PT } from '@/constants/jobCategories';
import { SuggestionPicker } from '@/components/SuggestionPicker';
import { JobWorkFields } from '@/components/JobWorkFields';

const EMPLOYMENT_TYPES = ['full-time', 'part-time', 'contract', 'internship', 'temporary'];

export default function CreateJob() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [credits, setCredits] = useState(null);
  const [publishingMode, setPublishingMode] = useState('free');

  const DESC_PLACEHOLDER = t('createJob.descPlaceholder');
  const REQ_PLACEHOLDER = t('createJob.reqPlaceholder');

  const [formData, setFormData] = useState({
    title_pt: '',
    description_pt: '',
    requirements_pt: '',
    location: '',
    salary_range_min: '',
    salary_range_max: '',
    salary_hidden: false,
    currency: 'CVE',
    category: '',
    employment_type: 'full-time',
    vacancies_count: 1,
    work_mode: 'presencial',
    contract_type: '',
    languages: []
  });

  // Business-category suggestions (based on employer's chosen sector)
  const [categoryData, setCategoryData] = useState(null);
  const [pickedSuggestion, setPickedSuggestion] = useState(null);
  // All business categories (for the per-job sector picker — lets multi-sector
  // employers like "Grupo Tavares" swap suggestions between Construção,
  // Digital, Hotelaria, etc. for each new job).
  const [allCategories, setAllCategories] = useState([]);
  const [suggestionSector, setSuggestionSector] = useState('');

  useEffect(() => {
    plansAPI.getMyCredits().then(r => {
      setCredits(r.data);
      if (!r.data?.has_free_available && (r.data?.unlimited || (r.data?.total_credits || 0) > 0)) {
        setPublishingMode('normal');
      }
    }).catch(() => {});
  }, []);

  // Load the full category list once so the employer can pick any sector.
  useEffect(() => {
    apiClient
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`)
      .then((r) => setAllCategories(r.data?.items || []))
      .catch(() => setAllCategories([]));
  }, []);

  // Initialise the per-job sector from the employer's profile once we know it.
  useEffect(() => {
    if (!suggestionSector && user?.business_category) {
      setSuggestionSector(user.business_category);
    }
  }, [user?.business_category, suggestionSector]);

  // Fetch suggestions whenever the per-job sector changes.
  useEffect(() => {
    if (!suggestionSector) { setCategoryData(null); return; }
    apiClient
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/categories/${suggestionSector}`)
      .then((r) => setCategoryData(r.data))
      .catch(() => setCategoryData(null));
  }, [suggestionSector]);

  // AI enrichment state — tracks the in-flight IA description request so we
  // (1) don't overwrite user edits, (2) allow cancelling if user clicks
  // another suggestion mid-flight.
  const [aiEnriching, setAiEnriching] = useState(false);
  const aiTokenRef = React.useRef(0);

  const applySuggestion = (s) => {
    setPickedSuggestion(s.title_pt);
    // 1) Instant feedback with the static template — user sees fields fill immediately.
    setFormData((prev) => ({
      ...prev,
      title_pt: s.title_pt,
      description_pt: s.description_template_pt || prev.description_pt,
      category: JOB_CATEGORY_TO_PT[s.job_category] || prev.category || 'Outro',
    }));
    // Scroll to form
    setTimeout(() => {
      const el = document.querySelector('[data-testid="job-title-input"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    // 2) Kick off IA in background to REPLACE the static template with a
    //    richer, contextual copy — but only if the user hasn't already
    //    edited it manually while we wait.
    const token = ++aiTokenRef.current;
    const staticTemplate = s.description_template_pt || '';
    setAiEnriching(true);
    apiClient.post(
      `/jobs/ai/description`,
      {
        title: s.title_pt,
        sector_slug: suggestionSector || null,
        category: JOB_CATEGORY_TO_PT[s.job_category] || null,
        location: null,
      },
      { timeout: 90000 }
    ).then(({ data }) => {
      // Race guard: only apply if this is still the latest suggestion click.
      if (aiTokenRef.current !== token) return;
      setFormData((prev) => {
        // If the user typed something different from the static template, respect their edit.
        const descChanged = prev.description_pt && prev.description_pt !== staticTemplate;
        const reqChanged = !!prev.requirements_pt;
        return {
          ...prev,
          description_pt: descChanged ? prev.description_pt : (data.description || prev.description_pt),
          requirements_pt: reqChanged ? prev.requirements_pt : (data.requirements || prev.requirements_pt),
        };
      });
    }).catch(() => { /* silent — user still has the static template */ })
      .finally(() => {
        if (aiTokenRef.current === token) setAiEnriching(false);
      });
  };

  const update = (k, v) => setFormData(prev => ({ ...prev, [k]: v }));

  /* ----------------------- Computed validation state ----------------------- */
  const REQUIRED_FIELDS = [
    { key: 'title_pt',        label: t('createJob.fieldTitle'),       miss: t('createJob.missTitle') },
    { key: 'category',        label: t('createJob.fieldCategory'),    miss: t('createJob.missCategory') },
    { key: 'location',        label: t('createJob.fieldLocation'),    miss: t('createJob.missLocation') },
    { key: 'description_pt',  label: t('createJob.fieldDescription'), miss: t('createJob.missDescription') },
    { key: 'requirements_pt', label: t('createJob.fieldRequirements'), miss: t('createJob.missRequirements') }
  ];

  const filledCount = useMemo(
    () => REQUIRED_FIELDS.filter(f => String(formData[f.key] || '').trim().length > 0).length,
    [formData] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const totalRequired = REQUIRED_FIELDS.length;
  const allFilled = filledCount === totalRequired;
  const missing = REQUIRED_FIELDS.filter(f => !String(formData[f.key] || '').trim());
  const completionPct = Math.round((filledCount / totalRequired) * 100);

  /* ----------------------- Submit handlers ----------------------- */
  // Salário opcional: campos vazios vão como null (o backend espera Optional[int]).
  const toSalary = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const buildPayload = () => ({
    ...formData,
    salary_range_min: toSalary(formData.salary_range_min),
    salary_range_max: toSalary(formData.salary_range_max),
    use_free: publishingMode === 'free',
    is_featured: publishingMode === 'featured'
  });
  const handleSaveDraft = async () => {
    if (!formData.title_pt && !formData.description_pt) {
      setMessage(`❌ ${t('createJob.errorNeedTitleForDraft')}`);
      return;
    }
    if (!formData.title_pt) {
      setMessage(`❌ ${t('createJob.errorNeedTitleForDraft2')}`);
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      await jobsAPI.create(buildPayload());
      setMessage(`✅ ${t('createJob.draftSaved')}`);
      setTimeout(() => navigate('/my-jobs'), 1500);
    } catch {
      setMessage(`❌ ${t('createJob.errorSaveJob')}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!allFilled) {
      const msgs = missing.map(m => m.miss);
      const human = msgs.length === 1
        ? msgs[0]
        : msgs.length === 2
          ? `${msgs[0]} e ${msgs[1].charAt(0).toLowerCase() + msgs[1].slice(1)}`
          : `${msgs.slice(0, -1).join(', ')} e ${msgs.at(-1).charAt(0).toLowerCase() + msgs.at(-1).slice(1)}`;
      setMessage(`❌ ${human} ${t('createJob.toPublishSuffix')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const salMin = formData.salary_range_min !== '' ? Number(formData.salary_range_min) : null;
    const salMax = formData.salary_range_max !== '' ? Number(formData.salary_range_max) : null;
    if (salMin !== null && salMax !== null && salMin > salMax) {
      setMessage(`❌ ${t('createJob.errorSalaryMinMax')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (publishingMode === 'normal' && !credits?.unlimited && (credits?.total_credits || 0) < 1) {
      setMessage(`❌ ${t('createJob.errorNoSimpleCredits')}`);
      return;
    }
    if (publishingMode === 'featured' && !credits?.unlimited && (credits?.featured_credits || 0) < 1) {
      setMessage(`❌ ${t('createJob.errorNoFeaturedCredits')}`);
      return;
    }
    if (publishingMode === 'free' && !credits?.has_free_available) {
      setMessage('');
      navigate('/checkout');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const { data } = await jobsAPI.create(buildPayload());
      const pub = await jobsAPI.publish(data.id);
      setMessage(pub?.data?.moderation ? `✅ ${t('myJobs.status.review')} — ${pub.data.message}` : `✅ ${t('createJob.published')}`);
      setTimeout(() => navigate('/my-jobs'), 1500);
    } catch (error) {
      const detail = error?.response?.data?.detail || t('createJob.errorPublishJob');
      setMessage(`❌ ${detail}`);
      setSaving(false);
    }
  };

  const needsCheckout = publishingMode === 'free' && credits && !credits.has_free_available;
  const publishCtaLabel = saving
    ? t('createJob.processing')
    : needsCheckout
      ? t('createJob.buyAndPublish')
      : publishingMode === 'free' ? t('createJob.publishFree') : publishingMode === 'featured' ? t('createJob.publishFeatured') : t('createJob.publishJob');

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="-ml-2 mb-3 text-gray-600" data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('createJob.backToDashboard')}
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">{t('createJob.title')}</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                {t('createJob.subtitle')}
              </p>
            </div>
            <div className="hidden lg:flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <Wallet className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-900">{credits?.unlimited ? 'Ilimitadas' : (credits?.total_credits || 0)}</span>
              <span className="text-gray-500">{t('createJob.credits')}</span>
              <span className="text-gray-300">·</span>
              <span className="font-medium text-amber-600">{credits?.unlimited ? 'Ilimitadas' : (credits?.featured_credits || 0)}</span>
              <span className="text-gray-500">{t('createJob.featured')}</span>
            </div>
          </div>
        </div>

        {message && (
          <Alert className={message.includes('✅') ? 'border-green-500 bg-green-50 mb-5' : 'border-red-500 bg-red-50 mb-5'}
                 variant={message.includes('✅') ? 'default' : 'destructive'}>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT — Form sections */}
          <div className="lg:col-span-2 space-y-5">

            {/* Category suggestions — per-job sector picker so multi-sector
                employers can switch between Construção, Digital, Hotelaria,
                etc. for each new job. */}
            {allCategories.length > 0 && (
              <Card className="p-5 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="category-suggestions">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 rounded-md bg-blue-600 text-white">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {categoryData ? t('createJob.suggestionsFor', { sector: categoryData.name_pt }) : t('createJob.suggestionsGeneric')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('createJob.suggestionsHint')}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <Label htmlFor="suggestion-sector" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    {t('createJob.sectorOfThisJob')}
                  </Label>
                  <select
                    id="suggestion-sector"
                    value={suggestionSector}
                    onChange={(e) => { setSuggestionSector(e.target.value); setPickedSuggestion(null); }}
                    className="mt-1 w-full sm:w-80 px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="suggestion-sector-select"
                  >
                    <option value="">{t('createJob.chooseSector')}</option>
                    {allCategories.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name_pt}</option>
                    ))}
                  </select>
                </div>

                {categoryData && categoryData.job_suggestions?.length > 0 ? (
                  <>
                    {aiEnriching && (
                      <div className="mb-3 text-xs font-semibold text-blue-700 flex items-center gap-2" data-testid="ai-enriching-badge">
                        <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                        {t('createJob.aiEnriching')}
                      </div>
                    )}
                    <SuggestionPicker key={suggestionSector} suggestions={categoryData.job_suggestions} picked={pickedSuggestion} onPick={applySuggestion} t={t} />
                  </>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {suggestionSector ? t('createJob.noSuggestionsForSector') : t('createJob.chooseSectorToSeeSuggestions')}
                  </p>
                )}
                <p className="mt-3 text-sm text-gray-700" data-testid="custom-role-hint">{t('createJob.customRoleHint')}</p>
              </Card>
            )}

            {/* Required legend */}
            <p className="text-xs text-gray-500 -mb-1">
              {t('createJob.requiredLegendPrefix')} <span className="text-red-500 font-semibold">*</span> {t('createJob.requiredLegendSuffix')}
            </p>

            {/* Section 1: Identidade */}
            <FormSection
              number={1}
              title={t('createJob.section1Title')}
              subtitle={t('createJob.section1Subtitle')}
            >
              <FieldGroup label={t('createJob.fieldTitle')} required hint={t('createJob.fieldTitleHint')}>
                <Input
                  value={formData.title_pt}
                  onChange={(e) => update('title_pt', e.target.value)}
                  placeholder={t('createJob.fieldTitlePlaceholder')}
                  data-testid="job-title-input"
                  maxLength={120}
                />
                <Counter value={formData.title_pt} max={120} ideal={[20, 80]} t={t} />
              </FieldGroup>

              <JobWorkFields formData={formData} setFormData={setFormData} testPrefix="job" />

              <div className="grid sm:grid-cols-2 gap-4">
                <FieldGroup label={t('createJob.fieldCategory')} required>
                  <select
                    value={formData.category}
                    onChange={(e) => update('category', e.target.value)}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="job-category-select"
                  >
                    <option value="">{t('createJob.selectPlaceholder')}</option>
                    {CATEGORIES.map((cat) => <option key={cat} value={cat}>{t(`jobSearch.categories.${cat}`)}</option>)}
                  </select>
                </FieldGroup>

                <FieldGroup label={t('createJob.fieldLocation')} required={formData.work_mode !== 'remoto'} hint={t('createJob.fieldLocationHint')}>
                  <Input
                    value={formData.location}
                    onChange={(e) => update('location', e.target.value)}
                    placeholder="Ex: Praia, Santiago"
                    data-testid="job-location-input"
                  />
                </FieldGroup>

                <FieldGroup label={t('createJob.fieldEmploymentType')} required>
                  <select
                    value={formData.employment_type}
                    onChange={(e) => update('employment_type', e.target.value)}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {EMPLOYMENT_TYPES.map((v) => <option key={v} value={v}>{t(`jobSearch.employmentTypes.${v}`)}</option>)}
                  </select>
                </FieldGroup>

                <FieldGroup label={t('createJob.fieldVacancies')} hint={t('createJob.fieldVacanciesHint')}>
                  <Input
                    type="number" min="1" max="50"
                    value={formData.vacancies_count}
                    onChange={(e) => update('vacancies_count', Math.max(1, Math.min(50, parseInt(e.target.value || '1', 10))))}
                    data-testid="job-vacancies-input"
                  />
                </FieldGroup>
              </div>
            </FormSection>

            {/* Section 2: Remuneração */}
            <FormSection
              number={2}
              title={t('createJob.section2Title')}
              subtitle={t('createJob.section2Subtitle')}
            >
              <div className="grid sm:grid-cols-3 gap-4">
                <FieldGroup label={t('createJob.fieldSalaryMin')} hint={t('createJob.fieldSalaryMinHint')}>
                  <Input
                    type="number" min="0"
                    value={formData.salary_range_min}
                    onChange={(e) => update('salary_range_min', e.target.value)}
                    placeholder="Ex: 50000"
                  />
                </FieldGroup>
                <FieldGroup label={t('createJob.fieldSalaryMax')}>
                  <Input
                    type="number" min="0"
                    value={formData.salary_range_max}
                    onChange={(e) => update('salary_range_max', e.target.value)}
                    placeholder="Ex: 80000"
                  />
                </FieldGroup>
                <FieldGroup label={t('createJob.fieldCurrency')}>
                  <select
                    value={formData.currency}
                    onChange={(e) => update('currency', e.target.value)}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CVE">CVE</option>
                    <option value="EUR">EUR</option>
                  </select>
                </FieldGroup>
              </div>
              {/* Toggle ocultar salário */}
              <label className="flex items-start gap-3 cursor-pointer select-none mt-1" data-testid="create-job-hide-salary-toggle">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!formData.salary_hidden}
                    onChange={(e) => update('salary_hidden', e.target.checked)}
                  />
                  <div className={`w-10 h-5 rounded-full transition-colors ${formData.salary_hidden ? 'bg-blue-600' : 'bg-gray-300'}`} />
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.salary_hidden ? 'translate-x-5' : ''}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{t('createJob.hideSalary')}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t('createJob.hideSalaryHint')}</p>
                </div>
              </label>
            </FormSection>

            {/* Section 3: Descrição + Requisitos */}
            <FormSection
              number={3}
              title={t('createJob.section3Title')}
              subtitle={t('createJob.section3Subtitle')}
              tipsTitle={t('createJob.tipsTitle')}
              tips={[
                t('createJob.tip1'),
                t('createJob.tip2'),
                t('createJob.tip3'),
                t('createJob.tip4')
              ]}
            >
              <FieldGroup label={t('createJob.fieldDescription')} required>
                <Textarea
                  value={formData.description_pt}
                  onChange={(e) => update('description_pt', e.target.value)}
                  rows={9}
                  placeholder={DESC_PLACEHOLDER}
                  data-testid="job-description-input"
                  className="font-normal"
                />
                <Counter value={formData.description_pt} ideal={[150, 1500]} t={t} />
              </FieldGroup>

              <FieldGroup label={t('createJob.fieldRequirements')} required>
                <Textarea
                  value={formData.requirements_pt}
                  onChange={(e) => update('requirements_pt', e.target.value)}
                  rows={7}
                  placeholder={REQ_PLACEHOLDER}
                  data-testid="job-requirements-input"
                />
                <Counter value={formData.requirements_pt} ideal={[80, 800]} t={t} />
              </FieldGroup>
            </FormSection>

            {/* Section 4: Modo de publicação */}
            <FormSection
              number={4}
              title={t('createJob.section4Title')}
              subtitle={t('createJob.section4Subtitle')}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ModeCard
                  mode="free" current={publishingMode} onClick={setPublishingMode}
                  icon={CheckCircle2}
                  title={t('createJob.modeFree')}
                  price="0 CVE"
                  description={credits?.has_free_available ? t('createJob.modeFreeAvailable') : t('createJob.modeFreeUsed')}
                  disabled={credits && !credits.has_free_available}
                />
                <ModeCard
                  mode="normal" current={publishingMode} onClick={setPublishingMode}
                  icon={Send}
                  title={t('createJob.modeSimple')}
                  price={t('createJob.oneCreditPrice')}
                  description={credits?.unlimited ? 'Vagas ilimitadas · Programa Pioneiro' : t('createJob.creditsAvailable', { count: credits?.total_credits || 0 })}
                  disabled={!credits?.unlimited && (credits?.total_credits || 0) < 1}
                />
                <ModeCard
                  mode="featured" current={publishingMode} onClick={setPublishingMode}
                  icon={Star}
                  title={t('createJob.modeFeatured')}
                  price={t('createJob.oneFeaturedPrice')}
                  description={credits?.unlimited ? 'Destaque ilimitado · Programa Pioneiro' : t('createJob.featuredAvailable', { count: credits?.featured_credits || 0 })}
                  highlight
                  disabled={!credits?.unlimited && (credits?.featured_credits || 0) < 1}
                />
              </div>

              {/* No-credits CTA */}
              {credits && !credits.has_free_available && !credits.unlimited && (credits.total_credits || 0) === 0 && (credits.featured_credits || 0) === 0 && (
                <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="no-credits-cta">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 mb-1">{t('createJob.noCreditsTitle')}</h4>
                      <p className="text-sm text-amber-800 mb-3">
                        {t('createJob.noCreditsBody')}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" onClick={() => navigate('/checkout?plan=simple')} className="bg-blue-600 hover:bg-blue-700" data-testid="buy-simple-btn">
                          <ShoppingBag className="h-3.5 w-3.5 mr-1.5" />{t('createJob.buySimple')}
                        </Button>
                        <Button size="sm" onClick={() => navigate('/checkout?plan=featured')} className="bg-amber-600 hover:bg-amber-700" data-testid="buy-featured-btn">
                          <Star className="h-3.5 w-3.5 mr-1.5" />{t('createJob.buyFeatured')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => navigate('/checkout')} data-testid="buy-more-btn">
                          {t('createJob.viewAllPlans')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </FormSection>

            {/* Desktop action row */}
            <div className="hidden lg:flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
                <Save className="h-4 w-4 mr-2" />{t('createJob.saveDraft')}
              </Button>
              <Button
                onClick={needsCheckout ? () => navigate('/checkout') : handlePublish}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 min-w-[200px]"
                data-testid="publish-job-btn"
              >
                <Send className="h-4 w-4 mr-2" />{publishCtaLabel}
              </Button>
            </div>
          </div>

          {/* RIGHT — Sticky Summary */}
          <aside className="hidden lg:block">
            <div className="sticky top-6 space-y-4">
              <Card className="p-5 border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-blue-600" />{t('createJob.summaryTitle')}
                </h3>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-gray-600">{t('createJob.completion')}</span>
                    <span className="font-semibold text-gray-900">{filledCount}/{totalRequired}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${allFilled ? 'bg-emerald-500' : 'bg-blue-500'}`}
                      style={{ width: `${completionPct}%` }}
                    />
                  </div>
                </div>

                {/* Required list */}
                <ul className="space-y-1.5 text-sm mb-4">
                  {REQUIRED_FIELDS.map(f => {
                    const done = String(formData[f.key] || '').trim().length > 0;
                    return (
                      <li key={f.key} className="flex items-center gap-2">
                        <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0 ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                          {done ? '✓' : '·'}
                        </span>
                        <span className={done ? 'text-gray-700' : 'text-gray-500'}>{f.label}</span>
                      </li>
                    );
                  })}
                </ul>

                {/* Mode summary */}
                <div className="p-3 rounded-md bg-gray-50 border border-gray-100 text-sm mb-4">
                  <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">{t('createJob.publishingModeLabel')}</div>
                  <div className="font-medium text-gray-900 flex items-center gap-1.5">
                    {publishingMode === 'free' && <><CheckCircle2 className="h-4 w-4 text-emerald-600" />{t('createJob.modeFree')}</>}
                    {publishingMode === 'normal' && <><Send className="h-4 w-4 text-blue-600" />{t('createJob.modeSimple')}</>}
                    {publishingMode === 'featured' && <><Star className="h-4 w-4 text-amber-500 fill-amber-500" />{t('createJob.modeFeatured')}</>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {publishingMode === 'free'      && (credits?.has_free_available ? t('createJob.modeFreeSummary') : t('createJob.modeFreeUsedSummary'))}
                    {publishingMode === 'normal'    && t('createJob.modeSimpleSummary')}
                    {publishingMode === 'featured'  && t('createJob.modeFeaturedSummary')}
                  </div>
                </div>

                {/* CTA */}
                <Button
                  onClick={needsCheckout ? () => navigate('/checkout') : handlePublish}
                  disabled={saving}
                  className="w-full bg-blue-600 hover:bg-blue-700 shadow-sm"
                  data-testid="publish-job-btn-desktop"
                >
                  <Send className="h-4 w-4 mr-2" />{publishCtaLabel}
                </Button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="w-full text-sm text-gray-600 hover:text-gray-900 mt-2 py-1.5"
                  data-testid="save-draft-link"
                >
                  {t('createJob.orSaveDraft')}
                </button>

                <p className="text-xs text-gray-500 mt-3 text-center">
                  {t('createJob.creditsDeductedNote')}
                </p>
              </Card>

              {/* Trust card */}
              <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 text-sm text-gray-700">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-gray-900">{t('createJob.tipLabel')}</strong> {t('createJob.tipBody')}
                  </p>
                </div>
              </Card>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 px-4 py-3 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
              <span className="font-semibold text-gray-900">{filledCount}/{totalRequired}</span>
              <span>{t('createJob.filled')}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${allFilled ? 'bg-emerald-500' : 'bg-blue-500'}`}
                style={{ width: `${completionPct}%` }}
              />
            </div>
          </div>
          <Button
            onClick={needsCheckout ? () => navigate('/checkout') : handlePublish}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 flex-shrink-0"
            data-testid="publish-job-btn-mobile"
          >
            <Send className="h-4 w-4 mr-1.5" />{saving ? '…' : needsCheckout ? t('createJob.buyShort') : t('createJob.publishShort')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ====================== Sub-components ====================== */

function FormSection({ number, title, subtitle, children, tipsTitle, tips }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <header className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-gray-900 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">
          {number}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{title}</h2>
          {subtitle && <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>}
        </div>
      </header>

      <div className="px-5 sm:px-6 pb-5 sm:pb-6 space-y-4">
        {children}
      </div>

      {tips && tips.length > 0 && (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6">
          <div className="rounded-lg bg-amber-50 border border-amber-100 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
              <Lightbulb className="h-4 w-4" />{tipsTitle}
            </div>
            <ul className="space-y-1 text-sm text-amber-900/90">
              {tips.map((tip, i) => <li key={i}>• {tip}</li>)}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

function FieldGroup({ label, required, hint, children }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <Label className="text-sm font-semibold text-gray-900 flex items-center gap-1">
          {label}
          {required && <span className="text-red-500" aria-label={t('createJob.required')}>*</span>}
        </Label>
        {!required && (
          <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{t('createJob.optional')}</span>
        )}
      </div>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <div>{children}</div>
    </div>
  );
}

function Counter({ value, max, ideal, t }) {
  const len = (value || '').length;
  let color = 'text-gray-400';
  let label = t('createJob.charsCount', { count: len });
  if (max) label = `${len} / ${max}`;
  if (ideal) {
    const [lo, hi] = ideal;
    if (len > 0 && len < lo) {
      color = 'text-amber-600';
      label += ` · ${t('createJob.idealFrom', { count: lo })}`;
    } else if (len >= lo && len <= hi) {
      color = 'text-emerald-600';
      label += ` · ${t('createJob.optimal')}`;
    } else if (len > hi) {
      color = 'text-amber-600';
      label += ` · ${t('createJob.above', { count: hi })}`;
    }
  }
  return <div className={`text-xs mt-1 ${color}`}>{label}</div>;
}

function ModeCard({ mode, current, onClick, icon, title, price, description, disabled, highlight }) {
  const Icon = icon;
  const active = current === mode;
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick(mode)}
      disabled={disabled}
      className={`p-4 rounded-lg text-left transition-all border-2 ${
        active
          ? highlight ? 'border-amber-500 bg-amber-50' : 'border-blue-600 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid={`publish-mode-${mode}`}
    >
      <div className="flex items-start justify-between mb-2">
        <Icon className={`h-5 w-5 ${active ? (highlight ? 'text-amber-600' : 'text-blue-600') : 'text-gray-500'} ${highlight && active ? 'fill-amber-500' : ''}`} />
        {active && <CheckCircle2 className={`h-5 w-5 ${highlight ? 'text-amber-600' : 'text-blue-600'}`} />}
      </div>
      <div className={`font-semibold text-sm ${active ? (highlight ? 'text-amber-900' : 'text-blue-700') : 'text-gray-900'}`}>{title}</div>
      <div className="text-base font-bold text-gray-900 mt-0.5">{price}</div>
      <div className="text-xs text-gray-600 mt-1">{description}</div>
    </button>
  );
}
